import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { AuthStore } from './auth/auth.types';
import { createAuthActions } from './auth/auth.actions';
import { authEventManager } from './auth/authEventManager';
import { parsePersistedOperator } from './auth/auth.operator';

export const useAuthStore = create<AuthStore>()(
  devtools(
    persist(
      (set, get) => ({
        user: null,
        session: null,
        simpleUser: null,
        activeOperator: null,
        loading: true,
        isAuthenticated: false,
        isInWaitingScreen: false,

        ...createAuthActions(set, get),
      }),
      {
        name: 'auth-storage',
        version: 4,
        migrate: (persistedState: unknown) => {
          const state = persistedState as Record<string, unknown> | null;

          const fromOperatorField = parsePersistedOperator(state?.activeOperator);
          const legacySimpleUser = state?.simpleUser as
            | { id?: string; username?: string; role?: unknown }
            | null
            | undefined;
          const fromLegacySimpleUser =
            legacySimpleUser?.id && legacySimpleUser?.username
              ? parsePersistedOperator({
                  type: 'simple_user',
                  id: legacySimpleUser.id,
                  username: legacySimpleUser.username,
                  role: legacySimpleUser.role,
                })
              : null;

          return {
            isInWaitingScreen: Boolean(state?.isInWaitingScreen),
            activeOperator: fromOperatorField ?? fromLegacySimpleUser,
          };
        },
        partialize: (state) => ({
          isInWaitingScreen: state.isInWaitingScreen,
          activeOperator: state.activeOperator,
        }),
      }
    ),
    {
      name: 'auth-store',
    }
  )
);

if (typeof window !== 'undefined') {
  let authInitialized = false;

  const initializeAuth = async () => {
    if (authInitialized) return;
    authInitialized = true;

    try {
      await useAuthStore.getState().initializeAuth();

      authEventManager.setAuthStateChangeCallback(async (event, session) => {
        await useAuthStore.getState().handleAuthStateChange(event, session);
      });

      window.addEventListener('beforeunload', cleanup);
    } catch (error) {
      console.error('[AuthStore] Initialization failed:', error);
      useAuthStore.setState({ loading: false });
    }
  };

  const cleanup = () => {
    authEventManager.cleanup();
  };

  const scheduleAuthInit = () => {
    initializeAuth().catch((error) => {
      console.error('[AuthStore] Critical error:', error);
      useAuthStore.setState({ loading: false });
    });
  };

  if (useAuthStore.persist.hasHydrated()) {
    scheduleAuthInit();
  } else {
    useAuthStore.persist.onFinishHydration(scheduleAuthInit);
  }
}

export const notifyOrganizationChange = () => {
  if (typeof window !== 'undefined') {
    setTimeout(() => {
      authEventManager.setupOrganizationClientListener();
    }, 100);
  }
};
